import React, { useState, useEffect, useRef } from 'react';
import qaData from '../assets/questions/qaQuestions.json';
import javaData from '../assets/questions/javaQuestions.json';
import dotnetData from '../assets/questions/netQuestions.json';
import databaseData from '../assets/questions/databaseQuestions.json';
import frontendData from '../assets/questions/frontendQuestions.json';
import backendData from '../assets/questions/backendQuestions.json';
import cloudData from '../assets/questions/cloudQuestions.json';
import devopsData from '../assets/questions/devopsQuestions.json';
import mlData from '../assets/questions/mlQuestions.json';
import mobileData from '../assets/questions/mobileQuestions.json';
import securityData from '../assets/questions/securityQuestions.json';
import projectManagementData from '../assets/questions/projectManagementQuestions.json';
import '../styles/Questions.css'; 
import { 
  FiChevronDown, FiCheckSquare, FiSquare, FiFilter, 
  FiSearch, FiX, FiPlus, FiClock, FiStar, FiSave, 
  FiCheck, FiList, FiEdit, FiTrash2, FiEye, FiArrowLeft
} from 'react-icons/fi';

const dataMapping = {
  QA: qaData,
  Java: javaData,
  '.NET': dotnetData,
  Database: databaseData,
  Frontend: frontendData,
  Backend: backendData,
  Cloud: cloudData,
  DevOps: devopsData,
  "Machine Learning": mlData,
  Mobile: mobileData,
  Security: securityData,
  "Project Management": projectManagementData,
};

// Example initial assessments (normally would come from an API/storage)
const initialAssessments = [
  {
    id: '1',
    name: 'Java Core Fundamentals',
    skill: 'Java',
    category: 'Core',
    questions: [
      'What are the main features of Java programming language?',
      'Explain the difference between JDK, JRE, and JVM.',
      'What is the difference between an abstract class and interface in Java?'
    ],
    duration: 30,
    difficulty: 'Medium',
    createdAt: new Date('2023-06-15').toISOString()
  }
];

function Questions() {
  // Previous state variables
  const [selectedSkill, setSelectedSkill] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [skillQuestions, setSkillQuestions] = useState([]);
  const [selectedQuestions, setSelectedQuestions] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [skillDropdownOpen, setSkillDropdownOpen] = useState(false);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [showAssessmentModal, setShowAssessmentModal] = useState(false);
  const [assessmentName, setAssessmentName] = useState('');
  const [assessmentDuration, setAssessmentDuration] = useState(30);
  const [assessmentDifficulty, setAssessmentDifficulty] = useState('Medium');
  const [successMessage, setSuccessMessage] = useState('');
  
  // New state variables for assessments panel
  const [currentView, setCurrentView] = useState('questions'); // 'questions', 'assessments', 'viewAssessment'
  const [assessments, setAssessments] = useState(initialAssessments);
  const [currentAssessment, setCurrentAssessment] = useState(null);
  const [assessmentSearchQuery, setAssessmentSearchQuery] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [assessmentToDelete, setAssessmentToDelete] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  
  // References
  const skillDropdownRef = useRef(null);
  const categoryDropdownRef = useRef(null);
  const modalRef = useRef(null);
  const deleteModalRef = useRef(null);

  const skills = Object.keys(dataMapping);

  // Load assessments from localStorage on initial load
  useEffect(() => {
    const savedAssessments = localStorage.getItem('assessments');
    if (savedAssessments) {
      try {
        setAssessments(JSON.parse(savedAssessments));
      } catch (e) {
        console.error('Error loading saved assessments:', e);
      }
    }
  }, []);

  // Add this to your component to validate data structure
useEffect(() => {
  // Validate data structure for all skills
  Object.entries(dataMapping).forEach(([skill, data]) => {
    try {
      if (!data.KeySkills || !Array.isArray(data.KeySkills)) {
        console.error(`Invalid data structure for ${skill}: KeySkills array missing`);
        return;
      }
      
      data.KeySkills.forEach(ks => {
        if (!ks.Category) {
          console.error(`Invalid category in ${skill}`);
        }
        
        if (!ks.Skills || !Array.isArray(ks.Skills)) {
          console.error(`Invalid Skills array in ${skill}/${ks.Category}`);
          return;
        }
        
        ks.Skills.forEach(skill => {
          if (!skill.Questions || !Array.isArray(skill.Questions)) {
            console.error(`Invalid Questions array in ${skill}/${ks.Category}`);
          }
        });
      });
    } catch (error) {
      console.error(`Error validating data structure for ${skill}:`, error);
    }
  });
}, []);

  // Save assessments to localStorage when they change
  useEffect(() => {
    localStorage.setItem('assessments', JSON.stringify(assessments));
  }, [assessments]);

  // Click outside handling for dropdowns
  useEffect(() => {
    function handleClickOutside(event) {
      if (skillDropdownRef.current && !skillDropdownRef.current.contains(event.target)) {
        setSkillDropdownOpen(false);
      }
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setCategoryDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Timer for success message
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);


// Add this to your JSX to display error messages
{errorMessage && (
  <div className="error-toast">
    <FiAlertTriangle />
    <span>{errorMessage}</span>
  </div>
)}

// Clear error message after a delay
useEffect(() => {
  if (errorMessage) {
    const timer = setTimeout(() => {
      setErrorMessage('');
    }, 5000);
    return () => clearTimeout(timer);
  }
}, [errorMessage]);

  // Get filtered questions for displaying in the modal
  // Updated getSelectedQuestionsArray function with better error handling and debugging
// Updated getSelectedQuestionsArray function to handle categories with spaces and special characters
const getSelectedQuestionsArray = () => {
  // For debugging
  console.log("Selected questions object:", selectedQuestions);
  
  const questions = [];
  
  // Process each selected question
  Object.entries(selectedQuestions)
    .filter(([_, isSelected]) => isSelected)
    .forEach(([key]) => {
      try {
        // Extract the skill and the rest from the key
        // We need a different approach since category names can contain hyphens
        const firstHyphenIndex = key.indexOf('-');
        if (firstHyphenIndex === -1) {
          console.error(`Invalid question key format (no hyphen): ${key}`);
          return; // Skip this question
        }
        
        const skill = key.substring(0, firstHyphenIndex);
        const rest = key.substring(firstHyphenIndex + 1);
        
        // Now find the last hyphen to extract the index
        const lastHyphenIndex = rest.lastIndexOf('-');
        if (lastHyphenIndex === -1) {
          console.error(`Invalid question key format (no index): ${key}`);
          return; // Skip this question
        }
        
        const category = rest.substring(0, lastHyphenIndex);
        const indexStr = rest.substring(lastHyphenIndex + 1);
        const index = parseInt(indexStr, 10);
        
        if (isNaN(index)) {
          console.error(`Invalid index in question key: ${key}`);
          return; // Skip this question
        }
        
        // Validate skill exists in dataMapping
        if (!dataMapping[skill]) {
          console.error(`Unknown skill in question key: ${skill}`);
          return; // Skip this question
        }
        
        // Get the questions for this specific skill and category
        const data = dataMapping[skill];
        const categoryData = data.KeySkills.find(ks => ks.Category === category);
        
        if (!categoryData) {
          console.error(`Category "${category}" not found in ${skill}`);
          return; // Skip this question
        }
        
        const skillQuestions = categoryData.Skills.flatMap(s => s.Questions || []);
        
        // Validate index is within range
        if (index < 0 || index >= skillQuestions.length) {
          console.error(`Index ${index} out of range for ${skill}/"${category}" questions (length: ${skillQuestions.length})`);
          return; // Skip this question
        }
        
        // Add the question to our result array
        const question = skillQuestions[index];
        if (question) {
          questions.push(question);
        } else {
          console.error(`Question at index ${index} is undefined for ${skill}/"${category}"`);
        }
      } catch (error) {
        console.error(`Error processing question key ${key}:`, error);
      }
    });
  
  console.log(`Extracted ${questions.length} questions for the assessment`);
  return questions;
};

  // Original functions for skill/category selection
  const getSkillCategories = (skill) => {
    const data = dataMapping[skill];
    return data.KeySkills.map(ks => ks.Category);
  };

  const getSkillQuestions = (skill, category) => {
    const data = dataMapping[skill];
    const selectedCategoryData = data.KeySkills.find(ks => ks.Category === category);
    return selectedCategoryData?.Skills.flatMap(skill => skill.Questions) || [];
  };

  const handleSkillClick = (skill) => {
    setSelectedSkill(skill);
    setSkillDropdownOpen(false);
    setSelectedCategory('');
    setSkillQuestions([]);
    setSelectedQuestions({});
  };

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setCategoryDropdownOpen(false);
    const questions = getSkillQuestions(selectedSkill, category);
    setSkillQuestions(questions);
  };

  const toggleQuestionSelection = (index) => {
    const questionKey = `${selectedSkill}-${selectedCategory}-${index}`;
    setSelectedQuestions(prevState => ({
      ...prevState,
      [questionKey]: !prevState[questionKey]
    }));
  };

  // Functions for assessment creation and management
  const handleAddToAssessment = () => {
    setAssessmentName(`${selectedSkill} - ${selectedCategory} Assessment`);
    setShowAssessmentModal(true);
  };

  const handleCreateAssessment = () => {
    try {
      // Get questions and validate we have some
      const questions = getSelectedQuestionsArray();
      
      if (questions.length === 0) {
        setErrorMessage('No valid questions could be added to the assessment. Please try selecting questions again.');
        return;
      }
      
      const newAssessment = {
        id: Date.now().toString(),
        name: assessmentName,
        skill: selectedSkill,
        category: selectedCategory,
        questions: questions,
        duration: assessmentDuration,
        difficulty: assessmentDifficulty,
        createdAt: new Date().toISOString()
      };
      
      console.log("Creating assessment with questions:", questions);
      
      setAssessments(prev => [newAssessment, ...prev]);
      setShowAssessmentModal(false);
      setSuccessMessage('Assessment created successfully!');
      
      // Optionally, switch to the assessments view after creation
      setCurrentView('assessments');
    } catch (error) {
      console.error("Error creating assessment:", error);
      setErrorMessage('An error occurred while creating the assessment.');
    }
  };

  const handleViewAssessment = (assessment) => {
    setCurrentAssessment(assessment);
    setCurrentView('viewAssessment');
  };

  const handleDeletePrompt = (assessment) => {
    setAssessmentToDelete(assessment);
    setShowDeleteConfirm(true);
  };

  const handleDeleteAssessment = () => {
    setAssessments(prev => prev.filter(a => a.id !== assessmentToDelete.id));
    setShowDeleteConfirm(false);
    setAssessmentToDelete(null);
    setSuccessMessage('Assessment deleted successfully');
    
    // If we're viewing the deleted assessment, go back to the list
    if (currentAssessment && currentAssessment.id === assessmentToDelete.id) {
      setCurrentView('assessments');
    }
  };

  const handleEditAssessment = (assessment) => {
    // In a real app, you'd populate the form with the assessment data
    // For now, just log that we want to edit
    console.log('Edit assessment:', assessment);
    setSuccessMessage('Edit functionality to be implemented');
  };

  const categories = selectedSkill ? getSkillCategories(selectedSkill) : [];
  
  // Filter questions based on search query
  const filteredQuestions = searchQuery 
    ? skillQuestions.filter(q => q.toLowerCase().includes(searchQuery.toLowerCase()))
    : skillQuestions;

  // Filter assessments based on search query
  const filteredAssessments = assessmentSearchQuery 
    ? assessments.filter(a => 
        a.name.toLowerCase().includes(assessmentSearchQuery.toLowerCase()) ||
        a.skill.toLowerCase().includes(assessmentSearchQuery.toLowerCase()) ||
        a.category.toLowerCase().includes(assessmentSearchQuery.toLowerCase())
      )
    : assessments;

  const selectedCount = Object.values(selectedQuestions).filter(Boolean).length;
  const selectedQuestionsArray = getSelectedQuestionsArray();

  // Format date for display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="questions-container">
      {/* Success message toast */}
      {successMessage && (
        <div className="success-toast">
          <FiCheck />
          <span>{successMessage}</span>
        </div>
      )}
    
      <div className="questions-header">
        {/* <h1>Question Bank</h1> */}
        <div className="view-tabs">
          <button 
            className={`view-tab ${currentView === 'questions' ? 'active' : ''}`}
            onClick={() => setCurrentView('questions')}
          >
            <FiFilter size={16} />
            Questions
          </button>
          <button 
            className={`view-tab ${currentView === 'assessments' || currentView === 'viewAssessment' ? 'active' : ''}`}
            onClick={() => setCurrentView('assessments')}
          >
            <FiList size={16} />
            Assessments ({assessments.length})
          </button>
        </div>
      </div>

      {/* Questions View */}
      {currentView === 'questions' && (
        <div className="questions-content">
          {/* Selection panel - same as before */}
          <div className="selection-panel">
            {/* Primary Skill Dropdown */}
            <div className="selection-group">
              <label className="selection-label">Primary Skill</label>
              <div className="dropdown-container" ref={skillDropdownRef}>
                <button 
                  className={`dropdown-button ${skillDropdownOpen ? 'active' : ''}`}
                  onClick={() => setSkillDropdownOpen(!skillDropdownOpen)}
                >
                  {selectedSkill || 'Select Primary Skill'}
                  <FiChevronDown className={`dropdown-icon ${skillDropdownOpen ? 'rotate' : ''}`} />
                </button>

                {skillDropdownOpen && (
                  <ul className="dropdown-list">
                    {skills.map((skill) => (
                      <li 
                        key={skill}
                        className={`dropdown-item ${selectedSkill === skill ? 'selected' : ''}`}
                        onClick={() => handleSkillClick(skill)}
                      >
                        {skill}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Category Dropdown - Only show when skill is selected */}
            {selectedSkill && (
              <div className="selection-group">
                <label className="selection-label">Skill Category</label>
                <div className="dropdown-container" ref={categoryDropdownRef}>
                  <button 
                    className={`dropdown-button ${categoryDropdownOpen ? 'active' : ''}`}
                    onClick={() => setCategoryDropdownOpen(!categoryDropdownOpen)}
                  >
                    {selectedCategory || 'Select Skill Category'}
                    <FiChevronDown className={`dropdown-icon ${categoryDropdownOpen ? 'rotate' : ''}`} />
                  </button>

                  {categoryDropdownOpen && categories.length > 0 && (
                    <ul className="dropdown-list">
                      {categories.map((category) => (
                        <li 
                          key={category}
                          className={`dropdown-item ${selectedCategory === category ? 'selected' : ''}`}
                          onClick={() => handleCategoryClick(category)}
                        >
                          {category}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Questions display - same as before */}
          {selectedCategory && skillQuestions.length > 0 && (
            <div className="questions-panel">
              <div className="questions-panel-header">
                <h2>Questions for {selectedCategory} in {selectedSkill}</h2>
                
                <div className="questions-controls">
                  <div className="search-container">
                    <FiSearch className="search-icon" />
                    <input
                      type="text"
                      placeholder="Search questions..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="search-input"
                    />
                    {searchQuery && (
                      <FiX 
                        className="clear-search" 
                        onClick={() => setSearchQuery('')}
                      />
                    )}
                  </div>
                  
                  <div className="selection-summary">
                    <FiFilter />
                    <span>{selectedCount} selected</span>
                  </div>
                </div>
              </div>

              {filteredQuestions.length > 0 ? (
                <div className="questions-grid">
                  {filteredQuestions.map((question, index) => {
                    const questionKey = `${selectedSkill}-${selectedCategory}-${index}`;
                    const isSelected = !!selectedQuestions[questionKey];
                    
                    return (
                      <div
                        key={index}
                        className={`question-card ${isSelected ? 'selected' : ''}`}
                        onClick={() => toggleQuestionSelection(index)}
                      >
                        <div className="question-checkbox">
                          {isSelected ? <FiCheckSquare size={18} /> : <FiSquare size={18} />}
                        </div>
                        <div className="question-text">{question}</div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="no-results">
                  {searchQuery ? (
                    <div className="empty-state">
                      <FiSearch size={48} />
                      <p>No questions match your search</p>
                      <button className="btn-text" onClick={() => setSearchQuery('')}>Clear search</button>
                    </div>
                  ) : (
                    <div className="empty-state">
                      <FiFilter size={48} />
                      <p>No questions available in this category</p>
                    </div>
                  )}
                </div>
              )}
              
              {selectedCount > 0 && (
                <div className="questions-action-panel">
                  <span>{selectedCount} question{selectedCount !== 1 ? 's' : ''} selected</span>
                  <button className="btn-primary" onClick={handleAddToAssessment}>
                    <FiPlus size={16} />
                    Add to Assessment
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Empty states - same as before */}
          {selectedSkill && !selectedCategory && (
            <div className="empty-selection-state">
              <FiFilter size={64} opacity={0.5} />
              <h3>Select a skill category to view questions</h3>
              <p>Choose from one of the available categories for {selectedSkill}</p>
            </div>
          )}

          {!selectedSkill && (
            <div className="empty-selection-state">
              <FiFilter size={64} opacity={0.5} />
              <h3>Select a primary skill to begin</h3>
              <p>Choose the primary skill to browse available question categories</p>
            </div>
          )}
        </div>
      )}

      {/* Assessments List View */}
      {currentView === 'assessments' && (
        <div className="assessments-container">
          <div className="assessments-header">
            <div className="search-container">
              <FiSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search assessments..."
                value={assessmentSearchQuery}
                onChange={(e) => setAssessmentSearchQuery(e.target.value)}
                className="search-input"
              />
              {assessmentSearchQuery && (
                <FiX 
                  className="clear-search" 
                  onClick={() => setAssessmentSearchQuery('')}
                />
              )}
            </div>
          </div>

          {filteredAssessments.length > 0 ? (
            <div className="assessments-list">
              {filteredAssessments.map(assessment => (
                <div key={assessment.id} className="assessment-card">
                  <div className="assessment-card-content">
                    <h3 className="assessment-name">{assessment.name}</h3>
                    <div className="assessment-meta">
                      <span className="skill-badge">{assessment.skill}</span>
                      <span className="category-badge">{assessment.category}</span>
                      <span className={`difficulty-badge ${assessment.difficulty.toLowerCase()}`}>
                        {assessment.difficulty}
                      </span>
                    </div>
                    <div className="assessment-details">
                      <div className="detail-item">
                        <FiClock size={14} />
                        <span>{assessment.duration} minutes</span>
                      </div>
                      <div className="detail-item">
                        <FiList size={14} />
                        <span>{assessment.questions.length} questions</span>
                      </div>
                      <div className="detail-item date">
                        Created on {formatDate(assessment.createdAt)}
                      </div>
                    </div>
                  </div>
                  <div className="assessment-card-actions">
                    <button 
                      className="icon-button" 
                      title="View Assessment"
                      onClick={() => handleViewAssessment(assessment)}
                    >
                      <FiEye size={16} />
                    </button>
                    <button 
                      className="icon-button" 
                      title="Edit Assessment"
                      onClick={() => handleEditAssessment(assessment)}
                    >
                      <FiEdit size={16} />
                    </button>
                    <button 
                      className="icon-button delete" 
                      title="Delete Assessment"
                      onClick={() => handleDeletePrompt(assessment)}
                    >
                      <FiTrash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-selection-state">
              <FiList size={64} opacity={0.5} />
              <h3>No assessments found</h3>
              {assessmentSearchQuery ? (
                <p>No assessments match your search criteria</p>
              ) : (
                <p>Create your first assessment by selecting questions from the Question Bank</p>
              )}
              {assessmentSearchQuery && (
                <button className="btn-text" onClick={() => setAssessmentSearchQuery('')}>
                  Clear search
                </button>
              )}
              {!assessmentSearchQuery && (
                <button className="btn-primary" onClick={() => setCurrentView('questions')}>
                  Go to Question Bank
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* View Assessment Detail */}
      {currentView === 'viewAssessment' && currentAssessment && (
        <div className="assessment-detail-container">
          <div className="assessment-detail-header">
            <button 
              className="back-button" 
              onClick={() => setCurrentView('assessments')}
            >
              <FiArrowLeft size={16} />
              Back to Assessments
            </button>
            <div className="assessment-actions">
              <button 
                className="btn-outline" 
                onClick={() => handleEditAssessment(currentAssessment)}
              >
                <FiEdit size={16} />
                Edit
              </button>
              <button 
                className="btn-outline danger" 
                onClick={() => handleDeletePrompt(currentAssessment)}
              >
                <FiTrash2 size={16} />
                Delete
              </button>
            </div>
          </div>
          <div className="assessment-detail-card">
            <div className="assessment-detail-header-content">
              <h2>{currentAssessment.name}</h2>
              <div className="assessment-meta">
                <span className="skill-badge large">{currentAssessment.skill}</span>
                <span className="category-badge large">{currentAssessment.category}</span>
                <span className={`difficulty-badge ${currentAssessment.difficulty.toLowerCase()} large`}>
                  {currentAssessment.difficulty}
                </span>
              </div>
              <div className="assessment-meta-details">
                <div className="meta-item">
                  <FiClock size={16} />
                  <span>{currentAssessment.duration} minutes</span>
                </div>
                <div className="meta-item">
                  <FiList size={16} />
                  <span>{currentAssessment.questions.length} questions</span>
                </div>
                <div className="meta-item">
                  Created on {formatDate(currentAssessment.createdAt)}
                </div>
              </div>
            </div>
            
            <div className="assessment-questions-list">
              <h3>Assessment Questions</h3>
              <div className="detail-questions-list">
                {currentAssessment.questions.map((question, index) => (
                  <div key={index} className="detail-question-item">
                    <span className="question-number">{index + 1}.</span>
                    <p className="question-text">{question}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assessment Creation Modal - with vertical scrollbar */}
      {showAssessmentModal && (
        <div className="modal-overlay">
          <div className="assessment-modal-exact" ref={modalRef}>
            {/* Header section with gray background */}
            <div className="modal-header-exact">
              <h3>Create New Assessment</h3>
              <button className="close-button-exact" onClick={() => setShowAssessmentModal(false)}>
                <FiX size={24} />
              </button>
            </div>

            <div className="modal-body-exact">
              {/* Assessment Name section */}
              <div className="form-field-container">
                <div className="form-label-exact">
                  <span>Assessment Name</span>
                </div>
                <div className="form-input-container">
                  <input
                    id="assessment-name"
                    type="text"
                    value={assessmentName}
                    onChange={(e) => setAssessmentName(e.target.value)}
                    className="form-input-exact"
                    placeholder="Enter assessment name"
                  />
                </div>
              </div>

              {/* Duration and Difficulty section */}
              <div className="form-field-container">
                {/* Two labels in one row */}
                <div className="form-label-group">
                  <div className="form-label-exact duration">
                    <FiClock size={18} /> Duration (minutes)
                  </div>
                  <div className="form-label-exact difficulty">
                    <FiStar size={18} /> Difficulty Level
                  </div>
                </div>
                
                {/* Two inputs in one row */}
                <div className="form-input-group">
                  <div className="input-container-half">
                    <input
                      id="assessment-duration"
                      type="number"
                      min="5"
                      max="180"
                      value={assessmentDuration}
                      onChange={(e) => setAssessmentDuration(Number(e.target.value))}
                      className="form-input-exact"
                    />
                  </div>
                  <div className="input-container-half">
                    <select
                      id="assessment-difficulty"
                      value={assessmentDifficulty}
                      onChange={(e) => setAssessmentDifficulty(e.target.value)}
                      className="form-select-exact"
                    >
                      <option value="Easy">Easy</option>
                      <option value="Medium">Medium</option>
                      <option value="Hard">Hard</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Selected Questions section - only visible when questions are selected */}
              {selectedQuestionsArray.length > 0 && (
                <div className="selected-questions-container">
                  <div className="form-label-exact">
                    <span>Selected Questions ({selectedQuestionsArray.length})</span>
                  </div>
                  <div className="questions-list-container">
                    {selectedQuestionsArray.map((question, index) => (
                      <div key={index} className="question-item">
                        <div className="question-number">{index + 1}.</div>
                        <div className="question-text">{question}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer buttons */}
            <div className="modal-footer-exact">
              <button 
                className="btn-cancel-exact"
                onClick={() => setShowAssessmentModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn-create-exact"
                onClick={handleCreateAssessment}
                disabled={!assessmentName.trim() || selectedQuestionsArray.length === 0}
              >
                <span className="btn-icon">
                  <FiSave size={18} />
                </span>
                Create Assessment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="delete-modal" ref={deleteModalRef}>
            <div className="modal-header">
              <h3>Confirm Deletion</h3>
              <button className="close-button" onClick={() => setShowDeleteConfirm(false)}>
                <FiX size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete the assessment "{assessmentToDelete.name}"?</p>
              <p className="warning">This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button 
                className="btn-secondary"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </button>
              <button 
                className="btn-danger"
                onClick={handleDeleteAssessment}
              >
                <FiTrash2 size={16} />
                Delete Assessment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Questions;